import { create, parseDependencies, type MathNode } from 'mathjs';
import { z } from 'zod';
import { calculationFormulaExpression, type KpiAggregationName, type KpiDirectionName } from './kpis';
import type { TrafficLight } from './status';

/**
 * Motor de cálculo de performance (PRD §14, §15, Anexo B; DEC-004 itens 1, 3, 4, 9 e 11; DEC-026).
 * Funções puras: a API usa para gravar os agregados (T-064) e a web para mostrar o mesmo número.
 * Performance é sempre um percentual (100 = meta atingida), limitado a [0, teto da empresa].
 */

// ---------------------------------------------------------------------------------------------
// Parâmetros por empresa

export const COMPANY_RULES = ['WEIGHTED', 'EQUAL'] as const;
export type CompanyRuleName = (typeof COMPANY_RULES)[number];
export const COMPANY_RULE_LABELS: Record<CompanyRuleName, string> = {
  WEIGHTED: 'Média ponderada pelos pesos das perspectivas',
  EQUAL: 'Média simples das perspectivas',
};

/** Parâmetros padrão (Anexo B e DEC-004): verde ≥ 95%, amarelo ≥ 80%, empresa ponderada, teto 200%. */
export const DEFAULT_PERFORMANCE_SETTINGS = {
  greenMin: 95,
  yellowMin: 80,
  companyRule: 'WEIGHTED' as CompanyRuleName,
  performanceCap: 200,
  /** Justificativa obrigatória quando o realizado fica vermelho (DEC-004 item 18). */
  requireRedJustification: true,
};

export const performanceSettingsSchema = z.object({
  greenMin: z.number(),
  yellowMin: z.number(),
  companyRule: z.enum(COMPANY_RULES),
  performanceCap: z.number(),
  requireRedJustification: z.boolean(),
  /** Quando foi alterado pela última vez (null = nunca: valem os padrões). */
  updatedAt: z.string().nullable(),
});
export type PerformanceSettings = z.infer<typeof performanceSettingsSchema>;

const percentField = (label: string) =>
  z.coerce
    .number({ message: `Informe ${label} em números.` })
    .min(0, 'Use de 0 a 100.')
    .max(100, 'Use de 0 a 100.')
    .multipleOf(0.01, 'Use no máximo duas casas decimais.');

export const performanceSettingsInputSchema = z
  .object({
    greenMin: percentField('o mínimo do verde'),
    yellowMin: percentField('o mínimo do amarelo'),
    companyRule: z.enum(COMPANY_RULES, { message: 'Escolha a regra da empresa.' }),
    performanceCap: z.coerce
      .number({ message: 'Informe o teto em números.' })
      .int('Use um número inteiro.')
      .min(100, 'O teto vai de 100% a 1000%.')
      .max(1000, 'O teto vai de 100% a 1000%.'),
    requireRedJustification: z.boolean().default(true),
  })
  .refine((v) => v.yellowMin <= v.greenMin, {
    message: 'O amarelo começa abaixo (ou no mesmo ponto) do verde.',
    path: ['yellowMin'],
  });
export type PerformanceSettingsInput = z.input<typeof performanceSettingsInputSchema>;

/** Faixas do semáforo (em % de performance). */
export type TrafficRule = { greenMin: number; yellowMin: number };

/** Regra do KPI: a própria (quando definida) ou a da empresa (DEC-004 item 4). */
export function effectiveTrafficRule(
  kpi: { trafficMode: 'COMPANY' | 'CUSTOM'; greenMin: number | null; yellowMin: number | null },
  company: TrafficRule,
): TrafficRule {
  return kpi.trafficMode === 'CUSTOM' && kpi.greenMin !== null && kpi.yellowMin !== null
    ? { greenMin: kpi.greenMin, yellowMin: kpi.yellowMin }
    : company;
}

// ---------------------------------------------------------------------------------------------
// Performance de um KPI num período

const clamp = (v: number, cap: number) => Math.min(cap, Math.max(0, v));
const round = (v: number) => Math.round(v * 100) / 100;

/**
 * "Quanto maior, melhor" contra uma referência: realizado ÷ meta × 100 (PRD §14.1). Meta negativa:
 * 100 + (realizado − meta) ÷ |meta| × 100. Meta zero: realizado 0 = 100%; acima de 0 = o teto; abaixo = 0.
 */
function higherBetter(actual: number, target: number, cap: number): number {
  if (target > 0) return (actual / target) * 100;
  if (target < 0) return 100 + ((actual - target) / Math.abs(target)) * 100;
  return actual > 0 ? cap : actual === 0 ? 100 : 0;
}

/**
 * "Quanto menor, melhor": meta ÷ realizado × 100 (PRD §14.2). Realizado zero (ou negativo) com meta
 * positiva = o melhor resultado possível: o teto da empresa (DEC-026). Meta zero: realizado 0 = 100%;
 * acima = 0. Meta negativa: 100 − (realizado − meta) ÷ |meta| × 100.
 */
function lowerBetter(actual: number, target: number, cap: number): number {
  if (target > 0) return actual > 0 ? (target / actual) * 100 : cap;
  if (target < 0) return 100 - ((actual - target) / Math.abs(target)) * 100;
  return actual <= 0 ? 100 : 0;
}

/**
 * Faixa ideal (PRD §14.3): dentro dos limites = 100%. Fora dela, com limite positivo, como "maior
 * melhor" contra o limite inferior (abaixo) ou "menor melhor" contra o superior (acima). Com limite
 * zero ou negativo (onde a razão não faz sentido), perde proporcionalmente à distância, medida em
 * larguras da faixa. Nunca passa de 100%: o melhor é estar na faixa (DEC-026).
 */
/** Fora da faixa, no máximo 99,99% (com o arredondamento em duas casas nunca vira 100%). */
const MAX_OUTSIDE_RANGE = 99.99;

function rangePerformance(actual: number, lower: number, upper: number, cap: number): number {
  if (actual >= lower && actual <= upper) return 100;
  const scale = upper > lower ? upper - lower : Math.max(Math.abs(lower), 1);
  let v: number;
  if (actual < lower) v = lower > 0 ? (actual / lower) * 100 : 100 - ((lower - actual) / scale) * 100;
  else v = upper > 0 ? lowerBetter(actual, upper, cap) : 100 - ((actual - upper) / scale) * 100;
  return Math.min(MAX_OUTSIDE_RANGE, v);
}

// Fórmula personalizada: avaliada aqui, sem executar código (percorre a árvore já conferida em kpis.ts).
const { parse } = create({ parseDependencies: parseDependencies! });
const FUNCTIONS: Record<string, (...a: number[]) => number> = {
  min: (...a) => Math.min(...a),
  max: (...a) => Math.max(...a),
  abs: (a = 0) => Math.abs(a),
  round: (a = 0, d = 0) => Math.round(a * 10 ** d) / 10 ** d,
  sqrt: (a = 0) => Math.sqrt(a),
};

function evaluateNode(node: MathNode, scope: Record<string, number>): number {
  const n = node as unknown as {
    type: string;
    value?: unknown;
    name?: string;
    op?: string;
    fn?: { name?: string } | string;
    args?: MathNode[];
    content?: MathNode;
  };
  switch (n.type) {
    case 'ConstantNode':
      if (typeof n.value !== 'number') throw new Error('constante');
      return n.value;
    case 'SymbolNode':
      if (!Object.hasOwn(scope, n.name!)) throw new Error('variável');
      return scope[n.name!]!;
    case 'ParenthesisNode':
      return evaluateNode(n.content!, scope);
    case 'OperatorNode': {
      const args = n.args!.map((a) => evaluateNode(a, scope));
      if (args.length === 1) {
        if (n.op === '-') return -args[0]!;
        if (n.op === '+') return args[0]!;
        throw new Error('operação');
      }
      const [a, b] = args as [number, number];
      if (n.op === '+') return a + b;
      if (n.op === '-') return a - b;
      if (n.op === '*') return a * b;
      if (n.op === '/') return a / b;
      if (n.op === '^') return a ** b;
      throw new Error('operação');
    }
    case 'FunctionNode': {
      const name = typeof n.fn === 'string' ? n.fn : (n.fn?.name ?? '');
      if (!Object.hasOwn(FUNCTIONS, name)) throw new Error('função');
      const fn = FUNCTIONS[name]!;
      return fn(...n.args!.map((a) => evaluateNode(a, scope)));
    }
    default:
      throw new Error('elemento');
  }
}

/**
 * Calcula a fórmula personalizada com `realizado` e `meta`. Resultado que não é um número finito
 * (divisão por zero, raiz de negativo) = null: o período fica sem performance (cinza).
 */
export function evaluatePerformanceFormula(formula: string, actual: number, target: number): number | null {
  try {
    const v = evaluateNode(parse(formula), { realizado: actual, meta: target });
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Calcula a fórmula de um KPI calculado (T-065) com o valor do mês de cada KPI de entrada (por código).
 * Entrada sem valor, ou resultado que não é um número finito (divisão por zero), = null: o mês fica sem
 * resultado.
 */
export function evaluateCalculationFormula(
  formula: string,
  values: Record<string, number | null>,
): number | null {
  const { expression, variables } = calculationFormulaExpression(formula);
  const scope: Record<string, number> = {};
  for (const [code, variable] of Object.entries(variables)) {
    const value = values[code];
    if (value === null || value === undefined) return null;
    scope[variable] = value;
  }
  try {
    const v = evaluateNode(parse(expression), scope);
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

export type KpiPerformanceInput = {
  direction: KpiDirectionName;
  actual: number | null;
  /** Meta do período (não usada na faixa ideal, que tem os próprios limites). */
  target: number | null;
  rangeLower?: number | null;
  rangeUpper?: number | null;
  performanceFormula?: string | null;
};

/**
 * Performance do KPI no período, em % (duas casas), limitada a [0, teto]. null = sem dados: sem
 * realizado ou sem meta (DEC-004 item 9), ou fórmula sem resultado numérico.
 */
export function kpiPerformance(
  input: KpiPerformanceInput,
  cap = DEFAULT_PERFORMANCE_SETTINGS.performanceCap,
): number | null {
  const { direction, actual, target } = input;
  if (actual === null || actual === undefined || !Number.isFinite(actual)) return null;
  if (direction === 'RANGE') {
    if (input.rangeLower === null || input.rangeLower === undefined) return null;
    if (input.rangeUpper === null || input.rangeUpper === undefined) return null;
    return round(clamp(rangePerformance(actual, input.rangeLower, input.rangeUpper, cap), cap));
  }
  if (target === null || target === undefined || !Number.isFinite(target)) return null;
  let value: number | null;
  if (direction === 'HIGHER_BETTER') value = higherBetter(actual, target, cap);
  else if (direction === 'LOWER_BETTER') value = lowerBetter(actual, target, cap);
  else
    value = input.performanceFormula
      ? evaluatePerformanceFormula(input.performanceFormula, actual, target)
      : null;
  return value === null ? null : round(clamp(value, cap));
}

// ---------------------------------------------------------------------------------------------
// Semáforo

/**
 * Cor do semáforo (PRD §15, Anexo B): azul = período futuro / planejamento; cinza = sem dados; verde,
 * amarelo e vermelho pelas faixas. "Menor melhor" já chega como "% da meta" (meta ÷ realizado), então
 * usa as mesmas faixas. Faixa ideal tem regra própria (Anexo B): só é verde dentro da faixa (100%);
 * fora dela, no máximo amarelo (DEC-026).
 */
export function trafficLight(
  performance: number | null,
  rule: TrafficRule,
  /** Direção do KPI; null para objetivo, perspectiva e empresa (médias, sem direção própria). */
  direction: KpiDirectionName | null,
  future = false,
): TrafficLight {
  if (future) return 'blue';
  if (performance === null) return 'gray';
  if (direction === 'RANGE' && performance < 100) return performance >= rule.yellowMin ? 'yellow' : 'red';
  if (performance >= rule.greenMin) return 'green';
  if (performance >= rule.yellowMin) return 'yellow';
  return 'red';
}

// ---------------------------------------------------------------------------------------------
// Ponderação: KPIs → objetivo → perspectiva → empresa

export type WeightedItem = { performance: number | null; weight: number | null };

/**
 * Pesos efetivos (DEC-004 item 3; DEC-026): os pesos informados valem como estão; os itens sem peso
 * dividem igualmente o que sobra até 100 (nada sobra = peso zero). Todos sem peso = pesos iguais.
 */
export function effectiveWeights(weights: (number | null)[]): number[] {
  const defined = weights.filter((w): w is number => w !== null);
  const missing = weights.length - defined.length;
  if (missing === 0) return weights as number[];
  if (defined.length === 0) return weights.map(() => 100 / weights.length);
  const share = Math.max(0, 100 - defined.reduce((s, w) => s + w, 0)) / missing;
  return weights.map((w) => (w === null ? share : w));
}

/**
 * Média ponderada (PRD §14.4): itens sem dados ficam de fora e os pesos dos demais são renormalizados
 * (DEC-004 item 9). Sem nenhum item com dados (ou só com peso zero) = null.
 */
export function weightedPerformance(items: WeightedItem[]): number | null {
  const weights = effectiveWeights(items.map((i) => i.weight));
  let sum = 0;
  let total = 0;
  items.forEach((item, i) => {
    if (item.performance === null) return;
    sum += item.performance * weights[i]!;
    total += weights[i]!;
  });
  return total > 0 ? round(sum / total) : null;
}

/** Performance do objetivo: média ponderada dos KPIs (RN-09). Só vínculos com objetivo ativo (DEC-026). */
export const objectivePerformance = weightedPerformance;
/** Performance da perspectiva: média ponderada dos objetivos (PRD §14.5, RN-22). */
export const perspectivePerformance = weightedPerformance;

/** Performance da empresa a partir das perspectivas, pela regra da empresa (PRD §14.6, DEC-004 item 1). */
export function companyPerformance(perspectives: WeightedItem[], rule: CompanyRuleName): number | null {
  return weightedPerformance(
    rule === 'EQUAL' ? perspectives.map((p) => ({ ...p, weight: null })) : perspectives,
  );
}

// ---------------------------------------------------------------------------------------------
// Acumulado / YTD e previsto x realizado

/**
 * Acumulado dos períodos pela agregação do KPI (DEC-004 item 11): soma, média ou último valor
 * lançado (a média ponderada pelas unidades, no tempo, é a média simples). Períodos sem valor ficam de
 * fora; nenhum valor = null.
 */
export function accumulate(values: (number | null)[], aggregation: KpiAggregationName): number | null {
  const present = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (present.length === 0) return null;
  if (aggregation === 'SUM') return present.reduce((s, v) => s + v, 0);
  if (aggregation === 'AVERAGE' || aggregation === 'WEIGHTED_AVERAGE')
    return present.reduce((s, v) => s + v, 0) / present.length;
  return present.at(-1)!;
}

/**
 * Consolidação entre unidades (T-067, DEC-037), com o valor de cada unidade já agregado no período: soma
 * (e "último valor", que soma o estoque de cada unidade), média simples ou média ponderada pelo peso da
 * unidade (sem peso cadastrado = 1). Nenhuma unidade com valor = null.
 */
export function consolidateUnits(
  entries: { unitId: string; value: number | null }[],
  aggregation: KpiAggregationName,
  weights?: ReadonlyMap<string, number>,
): number | null {
  const present = entries.filter(
    (e): e is { unitId: string; value: number } => e.value !== null && Number.isFinite(e.value),
  );
  if (present.length === 0) return null;
  if (aggregation === 'AVERAGE') return present.reduce((s, e) => s + e.value, 0) / present.length;
  if (aggregation === 'WEIGHTED_AVERAGE') {
    let sum = 0;
    let total = 0;
    for (const e of present) {
      const w = weights?.get(e.unitId) ?? 1;
      sum += w * e.value;
      total += w;
    }
    return total > 0 ? sum / total : null;
  }
  return present.reduce((s, e) => s + e.value, 0);
}

/** Linha do previsto x realizado (PRD §11.14): diferença absoluta, percentual e performance. */
export function plannedVsActual(
  input: KpiPerformanceInput,
  cap = DEFAULT_PERFORMANCE_SETTINGS.performanceCap,
): { difference: number | null; differencePercent: number | null; performance: number | null } {
  const { actual, target } = input;
  const both = actual !== null && actual !== undefined && target !== null && target !== undefined;
  return {
    difference: both ? actual - target : null,
    differencePercent: both && target !== 0 ? round(((actual - target) / Math.abs(target)) * 100) : null,
    performance: kpiPerformance(input, cap),
  };
}

// ---------------------------------------------------------------------------------------------
// Árvore completa do BSC num período

export type ScorecardInput = {
  perspectives: { id: string; weight: number | null; archived: boolean }[];
  /** parentId: objetivo que este desdobra (DEC-032: sem KPI próprio, o pai consolida os filhos). */
  objectives: {
    id: string;
    perspectiveId: string;
    weight: number | null;
    archived: boolean;
    parentId?: string | null;
  }[];
  /** Performance de cada KPI no período e o peso dele em cada objetivo que mede. */
  kpis: {
    id: string;
    performance: number | null;
    archived: boolean;
    objectives: { objectiveId: string; weight: number | null }[];
  }[];
  companyRule: CompanyRuleName;
};

/** De onde vem a performance do objetivo: dos KPIs próprios ou consolidada dos que o desdobram (DEC-032). */
export const OBJECTIVE_PERFORMANCE_SOURCES = ['KPIS', 'CHILDREN'] as const;
export type ObjectivePerformanceSource = (typeof OBJECTIVE_PERFORMANCE_SOURCES)[number];

export type ScorecardResult = {
  objectives: Record<string, number | null>;
  /** Origem da performance de cada objetivo ativo (null = sem KPI próprio e sem desdobramentos). */
  objectiveSources: Record<string, ObjectivePerformanceSource | null>;
  /**
   * Objetivo arquivado (DEC-052): a performance calculada só para consulta (detalhe do objetivo), pelos KPIs ativos
   * que o medem ou pelos objetivos ativos que o desdobram — fora da perspectiva e da empresa.
   */
  archivedObjectives: Record<string, number | null>;
  archivedSources: Record<string, ObjectivePerformanceSource | null>;
  perspectives: Record<string, number | null>;
  company: number | null;
};

/**
 * Performance de objetivos, perspectivas e empresa. Itens arquivados (KPI, objetivo, perspectiva)
 * ficam fora do cálculo — inclusive o vínculo de um KPI ativo com um objetivo arquivado (DEC-026).
 * Objetivo com KPI próprio: média ponderada dos KPIs (RN-09). Sem KPI próprio: média ponderada dos objetivos
 * ativos que o desdobram, subindo pela árvore (DEC-032). Perspectiva e empresa usam só os medidos por KPIs
 * próprios — o consolidado não conta duas vezes.
 */
export function computeScorecard(input: ScorecardInput): ScorecardResult {
  const active = input.objectives.filter((o) => !o.archived);
  const own = new Map<string, number | null>();
  for (const o of active) {
    const items = input.kpis
      .filter((k) => !k.archived)
      .flatMap((k) =>
        k.objectives
          .filter((l) => l.objectiveId === o.id)
          .map((l) => ({ performance: k.performance, weight: l.weight })),
      );
    if (items.length) own.set(o.id, objectivePerformance(items));
  }
  const childrenOf = new Map<string, typeof active>();
  for (const o of active) {
    if (o.parentId) childrenOf.set(o.parentId, [...(childrenOf.get(o.parentId) ?? []), o]);
  }
  const consolidated = new Map<string, number | null>();
  const visiting = new Set<string>();
  const performanceOf = (id: string): number | null => {
    if (own.has(id)) return own.get(id)!;
    if (consolidated.has(id)) return consolidated.get(id)!;
    // Ciclo (a API impede; aqui só por garantia): o nó do ciclo fica sem dados.
    if (visiting.has(id)) return null;
    visiting.add(id);
    const children = childrenOf.get(id) ?? [];
    const value = children.length
      ? weightedPerformance(children.map((c) => ({ performance: performanceOf(c.id), weight: c.weight })))
      : null;
    visiting.delete(id);
    consolidated.set(id, value);
    return value;
  };
  const objectives: Record<string, number | null> = {};
  const objectiveSources: Record<string, ObjectivePerformanceSource | null> = {};
  for (const o of active) {
    objectives[o.id] = performanceOf(o.id);
    objectiveSources[o.id] = own.has(o.id) ? 'KPIS' : childrenOf.has(o.id) ? 'CHILDREN' : null;
  }
  const archivedObjectives: Record<string, number | null> = {};
  const archivedSources: Record<string, ObjectivePerformanceSource | null> = {};
  for (const o of input.objectives.filter((x) => x.archived)) {
    const items = input.kpis
      .filter((k) => !k.archived)
      .flatMap((k) =>
        k.objectives
          .filter((l) => l.objectiveId === o.id)
          .map((l) => ({ performance: k.performance, weight: l.weight })),
      );
    const children = childrenOf.get(o.id) ?? [];
    archivedObjectives[o.id] = items.length
      ? objectivePerformance(items)
      : children.length
        ? weightedPerformance(children.map((c) => ({ performance: performanceOf(c.id), weight: c.weight })))
        : null;
    archivedSources[o.id] = items.length ? 'KPIS' : children.length ? 'CHILDREN' : null;
  }
  const perspectives: Record<string, number | null> = {};
  for (const p of input.perspectives) {
    if (p.archived) continue;
    // O objetivo sem KPI próprio entra como "sem dados", como antes: os pesos da perspectiva não mudam.
    perspectives[p.id] = perspectivePerformance(
      active
        .filter((o) => o.perspectiveId === p.id)
        .map((o) => ({ performance: own.get(o.id) ?? null, weight: o.weight })),
    );
  }
  const company = companyPerformance(
    input.perspectives
      .filter((p) => !p.archived)
      .map((p) => ({ performance: perspectives[p.id] ?? null, weight: p.weight })),
    input.companyRule,
  );
  return { objectives, objectiveSources, archivedObjectives, archivedSources, perspectives, company };
}
